import { useQuery, UseQueryOptions } from 'react-query';
import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeeRequestDto, EmployeeApiResponseDto } from '@personnel/application/dtos';

/**
 * Hook for fetching employees list within Personnel domain
 *
 * Uses composition layer for dependency injection.
 * Eliminates the need to create repositories in components.
 *
 * @param {EmployeeRequestDto} request - Request parameters (filters, pagination)
 * @param {UseQueryOptions} [options] - Optional React Query configuration
 * @returns {UseQueryResult<EmployeeApiResponseDto>} Query result with employees data
 *
 * @example
 * const { data, isLoading } = usePersonnelEmployees({
 *   search: 'john',
 *   page: 1,
 *   limit: 10
 * });
 *
 * @example
 * // With enabled option to control when query runs
 * const { data } = usePersonnelEmployees(request, { enabled: filtersReady });
 */
export const usePersonnelEmployees = (
  request: EmployeeRequestDto,
  options?: Omit<UseQueryOptions<EmployeeApiResponseDto, Error>, 'queryKey' | 'queryFn'>,
) => {
  const { getEmployees } = getEmployeeUseCases();

  return useQuery<EmployeeApiResponseDto, Error>({
    queryKey: ['personnel', 'employees', 'list', request],
    queryFn: () => getEmployees.execute(request),
    ...options,
  });
};
